import { useEffect, useState, useRef } from "react";
import moment from "moment";
import {
  EHappyHourMessageDisplayTiming,
  HappyHourMessage,
} from "@src/lib/happyHour/types/HappyHour.interface";
import { HAPPY_HOUR_WARNING_TIME_MINUTES } from "../../HappyHourConfig";

/**
 * Ce hook détermine si l'alerte HappyHour doit être affichée.
 * Il gère aussi un setTimeout pour afficher l'alerte au bon moment si besoin.
 */
export function useHappyHourAlertVisibility(message?: HappyHourMessage) {
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!message || !message.end_date) {
      setVisible(false);
      return;
    }

    const countDown = moment(message.end_date);
    const now = moment();
    const timeDiff = countDown.diff(now, "minutes");
    const isAllTime =
        message.display_timing === EHappyHourMessageDisplayTiming.ALL_TIME,
      isBeforeWarningTime =
        message.display_timing ===
        EHappyHourMessageDisplayTiming.BEFORE_WARNING_TIME, // temporalité finalement non utilisée...
      isWarningTime =
        message.display_timing === EHappyHourMessageDisplayTiming.WARNING_TIME; // alerte listing produits

    if (isBeforeWarningTime) {
      // Si le message doit être actif jusqu'à 20 minutes avant la fin,
      if (
        !countDown.isAfter(now) ||
        timeDiff < HAPPY_HOUR_WARNING_TIME_MINUTES
      ) {
        setVisible(false);
        return;
      }
    } else if (isWarningTime) {
      // Si la date est passée ou trop loin dans le futur, cacher l'alerte
      if (
        !countDown.isAfter(now) ||
        timeDiff > HAPPY_HOUR_WARNING_TIME_MINUTES
      ) {
        setVisible(false);
        return;
      }
    } else if (isAllTime) {
      // si la date est passée, cacher l'alerte
      if (!countDown.isAfter(now)) {
        setVisible(false);
        return;
      }
    }

    // Sinon, programmer l'affichage au bon moment
    const msToShow = countDown
      .clone()
      .subtract(HAPPY_HOUR_WARNING_TIME_MINUTES, "minutes")
      .diff(now, "milliseconds");
    if (msToShow > 0) {
      timeoutRef.current = setTimeout(() => {
        setVisible(isWarningTime || isAllTime);
      }, msToShow);
      setVisible(!isWarningTime || isAllTime);
    } else {
      setVisible(isWarningTime || isAllTime);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [message]);

  return visible;
}
